/** Escapes the five characters that matter in HTML text and quoted attributes. */
export function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Escapes user text for a LIKE / ILIKE pattern: `\`, `%` and `_` lose their special meaning.
 * Use it with `.ilike(column, `%${escapeLikePattern(text)}%`)`.
 */
export function escapeLikePattern(value: string): string {
  return value.replace(/[\\%_]/g, (char) => `\\${char}`);
}

/**
 * Prepares user text to be interpolated into a PostgREST `.or()` / `.and()` filter string.
 * Those strings use `,` `(` `)` `.` `:` as syntax, so a value containing them could add
 * terms to the filter; here they are replaced by spaces (so are `*`, quotes and backslashes)
 * and `%` and `_` are escaped so they match themselves. The result is wrapped by the caller:
 * `.or(`first_name.ilike.%${escapePostgrestSearch(text)}%`)`.
 */
export function escapePostgrestSearch(value: string): string {
  const withoutSyntax = value.replace(/[,()*"\\:]/g, " ").replace(/\s+/g, " ").trim();
  return escapeLikePattern(withoutSyntax);
}

/**
 * Neutralises a cell for CSV/Excel: a value starting with `=`, `+`, `-`, `@`, tab or
 * carriage return would run as a formula when the file is opened, so it gets a leading `'`.
 */
export function neutralizeCsvCell(value: string): string {
  if (!/^[=+\-@\t\r]/.test(value)) return value;
  // A plain phone number or a negative number starts with + or - but is not a formula.
  if (/^[+-]?[\d\s().-]+$/.test(value) && /\d/.test(value)) return value;
  return `'${value}`;
}

const SAFE_EXTENSION = /^[a-z0-9]{1,8}$/;

/**
 * File extension from a name, accepted only if it is in the allowlist; otherwise the
 * fallback. The name is never trusted: `evil.html` with allowed ["png","jpg"] gives the fallback.
 */
export function safeFileExtension(fileName: string, allowed: readonly string[], fallback: string): string {
  const raw = fileName.includes(".") ? (fileName.split(".").pop() ?? "").toLowerCase() : "";
  return SAFE_EXTENSION.test(raw) && allowed.includes(raw) ? raw : fallback;
}

/**
 * A file name that is safe to store or to put in a path: no directories, no control
 * characters, no leading dots, only letters, digits, dot, dash and underscore, at most
 * 120 characters. Accents are removed. Falls back to "file" when nothing is left.
 */
export function safeFileName(fileName: string): string {
  const base = fileName.split(/[\\/]/).pop() ?? "";
  const cleaned = base
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/^[._]+/, "")
    .replace(/_+/g, "_")
    .slice(0, 120);
  return cleaned === "" ? "file" : cleaned;
}
